import { useState, useMemo, useCallback } from 'react'
import {
  ChevronLeft, ChevronRight, Plus, Search, Info, X,
  Clock, MapPin, ChevronRight as ChevronRightIcon,
} from 'lucide-react'
import {
  useCalendarEvents, useUpcomingEvents,
  CATEGORY_META, formatEventTime, getWIBDateString,
  type EventCategory, type NewCalendarEvent,
} from '@/hooks/useCalendarEvents'
import { cn } from '@/lib/utils'

const MONTHS_ID = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember']
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agt','Sep','Okt','Nov','Des']
const DAYS_HEADER = ['Sen','Sel','Rab','Kam','Jum','Sab','Min']
const DAY_FULL = ['Minggu','Senin','Selasa','Rabu','Kamis','Jumat','Sabtu']
const VIEWS = ['Bulan','Minggu','Hari','Agenda'] as const
type ViewType = typeof VIEWS[number]

const DIVISI_OPTIONS = ['Semua Divisi / Penyelenggara','PP','ME','OA','OPS','OD','Digital']
const CATEGORIES = Object.entries(CATEGORY_META) as [EventCategory, typeof CATEGORY_META[EventCategory]][]

function buildCalendarDays(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1)
  const totalDays = new Date(year, month + 1, 0).getDate()
  const startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1

  const days: Date[] = []
  for (let i = startDow; i > 0; i--) days.push(new Date(year, month, 1 - i))
  for (let d = 1; d <= totalDays; d++) days.push(new Date(year, month, d))
  let next = 1
  while (days.length % 7 !== 0) days.push(new Date(year, month + 1, next++))
  return days
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth() &&
         a.getDate() === b.getDate()
}

function isToday(d: Date) {
  return isSameDay(d, new Date())
}

// ── Add Event Modal ────────────────────────────────────────────────────────────
interface AddModalProps {
  defaultDate?: string
  onClose: () => void
  onAdd: (e: NewCalendarEvent) => Promise<{ error: unknown }>
}

function AddEventModal({ defaultDate, onClose, onAdd }: AddModalProps) {
  const today = defaultDate ?? new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({
    title: '', category: 'rapat_meeting' as EventCategory,
    startDate: today, startTime: '09:00',
    endDate: today,   endTime: '10:00',
    location: '', divisi: '', description: '', is_external: false,
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }))

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.title.trim()) { setErr('Judul event wajib diisi.'); return }
    setSaving(true)
    const { error } = await onAdd({
      title:       form.title.trim(),
      description: form.description || undefined,
      start_dt:    `${form.startDate}T${form.startTime}:00+07:00`,
      end_dt:      `${form.endDate}T${form.endTime}:00+07:00`,
      location:    form.location || undefined,
      category:    form.category,
      divisi:      form.divisi || undefined,
      is_external: form.is_external,
    })
    setSaving(false)
    if (error) { setErr('Gagal menyimpan event. Coba lagi.'); return }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-cerah-navy">Tambah Event Baru</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5 flex flex-col gap-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Judul Event <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="Masukkan judul event..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal"
              required
            />
          </div>

          {/* Category + Divisi */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Kategori <span className="text-red-500">*</span></label>
              <select
                value={form.category}
                onChange={e => set('category', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal bg-white text-gray-700"
              >
                {CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Divisi / Penyelenggara</label>
              <select
                value={form.divisi}
                onChange={e => set('divisi', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal bg-white text-gray-700"
              >
                <option value="">Semua / Umum</option>
                {['PP','ME','OA','OPS','OD','Digital'].map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
          </div>

          {/* Start date + time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Tanggal Mulai <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={form.startDate}
                onChange={e => { set('startDate', e.target.value); if (e.target.value > form.endDate) set('endDate', e.target.value) }}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Waktu Mulai <span className="text-red-500">*</span></label>
              <input
                type="time"
                value={form.startTime}
                onChange={e => set('startTime', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal"
                required
              />
            </div>
          </div>

          {/* End date + time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Tanggal Selesai <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={form.endDate}
                min={form.startDate}
                onChange={e => set('endDate', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Waktu Selesai <span className="text-red-500">*</span></label>
              <input
                type="time"
                value={form.endTime}
                onChange={e => set('endTime', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal"
                required
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Lokasi</label>
            <input
              type="text"
              value={form.location}
              onChange={e => set('location', e.target.value)}
              placeholder="Ruang rapat, Online (Zoom), dll."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Deskripsi</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Agenda, tujuan, atau informasi tambahan..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal resize-none"
            />
          </div>

          {/* External toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_external}
              onChange={e => set('is_external', e.target.checked)}
              className="w-4 h-4 rounded accent-cerah-teal"
            />
            <span className="text-sm text-gray-600">Event eksternal (pihak luar CERAH)</span>
          </label>

          {err && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-md">{err}</p>}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Batal
          </button>
          <button
            onClick={handleSubmit as unknown as React.MouseEventHandler}
            disabled={saving}
            className="px-5 py-2 text-sm font-semibold bg-cerah-teal text-white rounded-lg hover:bg-cerah-teal/90 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Menyimpan...' : 'Simpan Event'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function KalenderEvent() {
  const today = new Date()
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const [view, setView]               = useState<ViewType>('Bulan')
  const [showModal, setShowModal]     = useState(false)
  const [filterCat, setFilterCat]     = useState<EventCategory | ''>('')
  const [filterDiv, setFilterDiv]     = useState('')
  const [filterSearch, setFilterSearch] = useState('')

  const year  = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const { events, loading, addEvent } = useCalendarEvents(year, month)
  const { events: upcoming }          = useUpcomingEvents(5)

  const goBack  = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))
  const goNext  = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))
  const goToday = () => setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1))

  const calDays = useMemo(() => buildCalendarDays(year, month), [year, month])

  // Events for current month only (for category count)
  const currentMonthEvents = useMemo(() =>
    events.filter(e => {
      const d = new Date(e.start_dt)
      return d >= new Date(year, month, 1) && d < new Date(year, month + 1, 1)
    }), [events, year, month])

  const categoryCounts = useMemo(() => {
    const counts = {} as Record<EventCategory, number>
    for (const e of currentMonthEvents) {
      counts[e.category] = (counts[e.category] ?? 0) + 1
    }
    return counts
  }, [currentMonthEvents])

  // Filtered events for agenda/sidebar
  const filteredEvents = useMemo(() => {
    return currentMonthEvents.filter(e => {
      if (filterCat && e.category !== filterCat) return false
      if (filterDiv && filterDiv !== 'Semua Divisi / Penyelenggara' && e.divisi !== filterDiv) return false
      if (filterSearch && !e.title.toLowerCase().includes(filterSearch.toLowerCase())) return false
      return true
    })
  }, [currentMonthEvents, filterCat, filterDiv, filterSearch])

  const getEventsForDay = useCallback((day: Date) => {
    const dayStr = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`
    return events.filter(e => getWIBDateString(e.start_dt) === dayStr)
  }, [events])

  const handleAdd = useCallback(async (e: NewCalendarEvent) => {
    return addEvent(e)
  }, [addEvent])

  // Group agenda events by date
  const agendaGroups = useMemo(() => {
    const groups: Record<string, typeof filteredEvents> = {}
    for (const e of filteredEvents) {
      const key = getWIBDateString(e.start_dt)
      if (!groups[key]) groups[key] = []
      groups[key].push(e)
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredEvents])

  return (
    <div className="flex h-full">

      {/* ── Main ── */}
      <div className="flex-1 min-w-0 overflow-y-auto p-6 flex flex-col gap-4">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-gray-400">
          <span>What's Up CERAH</span>
          <ChevronRightIcon size={12} />
          <span className="text-gray-600 font-medium">Kalender Event</span>
        </nav>

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-cerah-navy">Kalender Event</h1>
            <p className="text-sm text-gray-500 mt-0.5">Jadwal kegiatan dan event internal maupun eksternal CERAH.</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="shrink-0 flex items-center gap-1.5 px-4 py-2 bg-cerah-teal text-white text-sm font-semibold rounded-lg hover:bg-cerah-teal/90 transition-colors"
          >
            <Plus size={15} />
            Tambah Event
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <button onClick={goBack}  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-gray-500"><ChevronLeft  size={16} /></button>
            <button onClick={goNext}  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-gray-500"><ChevronRight size={16} /></button>
            <button onClick={goToday} className="ml-1 px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-600">Hari ini</button>
          </div>
          <span className="text-base font-bold text-cerah-navy flex-1">
            {MONTHS_ID[month]} {year}
          </span>
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            {VIEWS.map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium transition-colors',
                  view === v
                    ? 'bg-cerah-teal text-white'
                    : 'text-gray-500 hover:bg-gray-50'
                )}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* ── Bulan view ── */}
        {view === 'Bulan' && (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
              {DAYS_HEADER.map((d, i) => (
                <div key={d} className={cn('py-2 text-center text-xs font-semibold', i === 6 ? 'text-red-400' : 'text-gray-500')}>
                  {d}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7">
              {calDays.map((day, idx) => {
                const isCurrentMonth = day.getMonth() === month
                const isThisToday    = isToday(day)
                const isSunday       = idx % 7 === 6
                const dayEvents      = getEventsForDay(day)
                const overflow       = dayEvents.length > 2 ? dayEvents.length - 2 : 0

                return (
                  <div
                    key={idx}
                    className={cn(
                      'min-h-[110px] p-1.5 border-b border-r border-gray-100 flex flex-col gap-1',
                      !isCurrentMonth && 'bg-gray-50/60',
                      isThisToday && 'bg-cerah-teal-light/30',
                    )}
                  >
                    {/* Date number */}
                    <div className="flex justify-end">
                      <span className={cn(
                        'w-6 h-6 flex items-center justify-center text-xs font-semibold rounded-full',
                        isThisToday   ? 'bg-cerah-teal text-white' :
                        !isCurrentMonth ? 'text-gray-300' :
                        isSunday      ? 'text-red-400' : 'text-gray-700',
                      )}>
                        {day.getDate()}
                      </span>
                    </div>

                    {/* Events */}
                    {dayEvents.slice(0, 2).map(ev => {
                      const meta = CATEGORY_META[ev.category]
                      return (
                        <div key={ev.id} className="flex flex-col gap-0.5">
                          <div className={cn('flex items-center gap-1', meta.text)}>
                            <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', meta.dot)} />
                            <span className="text-[10px] font-medium">{formatEventTime(ev.start_dt)}</span>
                          </div>
                          <p className="text-[10px] text-gray-700 leading-tight line-clamp-2 pl-2.5">{ev.title}</p>
                        </div>
                      )
                    })}
                    {overflow > 0 && (
                      <span className="text-[10px] text-cerah-teal font-medium pl-2.5">+{overflow} lainnya</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ── Agenda view ── */}
        {view === 'Agenda' && (
          <div className="flex flex-col gap-4">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-cerah-teal border-t-transparent rounded-full animate-spin" />
              </div>
            ) : agendaGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-sm gap-2">
                <p>Tidak ada event untuk bulan ini.</p>
              </div>
            ) : (
              agendaGroups.map(([dateStr, evs]) => {
                const d = new Date(dateStr + 'T00:00:00')
                return (
                  <div key={dateStr}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-bold text-cerah-navy">
                        {DAY_FULL[d.getDay()]}, {d.getDate()} {MONTHS_ID[d.getMonth()]} {d.getFullYear()}
                      </span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                    <div className="flex flex-col gap-2 pl-2">
                      {evs.map(ev => {
                        const meta = CATEGORY_META[ev.category]
                        return (
                          <div key={ev.id} className={cn('flex items-start gap-3 p-3 rounded-xl border', meta.bg, meta.border)}>
                            <span className={cn('w-2 h-2 rounded-full mt-1 shrink-0', meta.dot)} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-cerah-navy">{ev.title}</p>
                              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Clock size={11} />
                                  {formatEventTime(ev.start_dt)} – {formatEventTime(ev.end_dt)} WIB
                                </span>
                                {ev.location && (
                                  <span className="flex items-center gap-1">
                                    <MapPin size={11} />
                                    {ev.location}
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full', meta.text, meta.bg, meta.border, 'border')}>
                              {meta.label}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        )}

        {/* ── Minggu / Hari: placeholder ── */}
        {(view === 'Minggu' || view === 'Hari') && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 text-sm gap-2 border border-dashed border-gray-200 rounded-xl">
            <p className="font-medium">Tampilan {view} segera hadir.</p>
            <p className="text-xs">Gunakan tampilan Bulan atau Agenda untuk melihat event.</p>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-xs text-gray-500 pt-1">
          {CATEGORIES.map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className={cn('w-2.5 h-2.5 rounded-full', v.dot)} />
              {v.label}
            </span>
          ))}
        </div>

        {/* Info note */}
        <div className="flex items-start gap-3 p-3.5 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700">
          <Info size={15} className="shrink-0 mt-0.5 text-blue-500" />
          <div>
            <p className="font-semibold mb-0.5">Informasi</p>
            <p className="text-blue-600">
              Anda dapat menambahkan event baru ke kalender untuk memastikan semua kegiatan tercatat dan dapat diakses oleh tim.
              Klik tombol <strong>Tambah Event</strong> untuk membuat event baru.
            </p>
          </div>
        </div>
      </div>

      {/* ── Right sidebar ── */}
      <aside className="w-72 shrink-0 border-l border-gray-100 overflow-y-auto p-5 flex flex-col gap-5 bg-white">

        {/* Event Mendatang */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-cerah-navy">Event Mendatang</h3>
            <button
              onClick={() => { setView('Agenda'); goToday() }}
              className="text-[10px] text-cerah-teal hover:underline font-medium"
            >
              Lihat Semua
            </button>
          </div>
          <div className="flex flex-col gap-3">
            {upcoming.length === 0 ? (
              <p className="text-xs text-gray-400">Tidak ada event mendatang.</p>
            ) : upcoming.map(ev => {
              const d = new Date(ev.start_dt)
              const meta = CATEGORY_META[ev.category]
              return (
                <div key={ev.id} className="flex gap-3 items-start">
                  <div className="shrink-0 text-center min-w-[36px]">
                    <p className="text-[10px] font-bold text-cerah-teal uppercase">{MONTHS_SHORT[d.getMonth()]}</p>
                    <p className="text-xl font-bold text-cerah-navy leading-none">{d.getDate()}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className={cn('w-2 h-2 rounded-full shrink-0', meta.dot)} />
                      <p className="text-xs font-semibold text-cerah-navy truncate">{ev.title}</p>
                    </div>
                    <p className="text-[10px] text-gray-400">
                      {new Intl.DateTimeFormat('id-ID', { weekday:'long', day:'numeric', month:'long', year:'numeric', timeZone:'Asia/Jakarta' }).format(new Date(ev.start_dt))}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {formatEventTime(ev.start_dt)} – {formatEventTime(ev.end_dt)} WIB
                    </p>
                    {ev.location && <p className="text-[10px] text-gray-400 truncate">{ev.location}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="h-px bg-gray-100" />

        {/* Filter Event */}
        <div>
          <h3 className="text-xs font-bold text-cerah-navy mb-3">Filter Event</h3>
          <div className="flex flex-col gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                placeholder="Cari event..."
                value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal"
              />
            </div>
            <select
              value={filterCat}
              onChange={e => setFilterCat(e.target.value as EventCategory | '')}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal bg-white text-gray-600"
            >
              <option value="">Semua Jenis Event</option>
              {CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select
              value={filterDiv}
              onChange={e => setFilterDiv(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal bg-white text-gray-600"
            >
              {DIVISI_OPTIONS.map(d => <option key={d}>{d}</option>)}
            </select>
            {(filterCat || filterDiv || filterSearch) && (
              <button
                onClick={() => { setFilterCat(''); setFilterDiv(''); setFilterSearch('') }}
                className="w-full py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset Filter
              </button>
            )}
          </div>
        </div>

        <div className="h-px bg-gray-100" />

        {/* Kategori Event */}
        <div>
          <h3 className="text-xs font-bold text-cerah-navy mb-3">Kategori Event</h3>
          <div className="flex flex-col gap-2">
            {CATEGORIES.map(([k, v]) => (
              <button
                key={k}
                onClick={() => setFilterCat(filterCat === k ? '' : k)}
                className={cn(
                  'flex items-center justify-between px-2 py-1.5 rounded-lg transition-colors',
                  filterCat === k ? `${v.bg} ${v.border} border` : 'hover:bg-gray-50'
                )}
              >
                <span className="flex items-center gap-2 text-xs text-gray-600">
                  <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', v.dot)} />
                  {v.label}
                </span>
                <span className="text-xs font-bold text-gray-500">{categoryCounts[k] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>

      </aside>

      {/* Add Event Modal */}
      {showModal && (
        <AddEventModal
          onClose={() => setShowModal(false)}
          onAdd={handleAdd}
        />
      )}
    </div>
  )
}
